// Centralized dashboard UI translations. Dot-path keys, resolved by useLang().t().
// Only static UI chrome is translated; dynamic DB content (dish names) stays as stored.

const STRINGS = {
  en: {
    common: {
      cancel: 'Cancel', save: 'Save', create: 'Create', update: 'Update', delete: 'Delete',
      edit: 'Edit', remove: 'Remove', refresh: 'Refresh', loading: 'Loading...', saving: 'Saving...',
      active: 'Active', inactive: 'Inactive', yes: 'Yes', no: 'No', close: 'Close', back: 'Back',
      search: 'Search', all: 'All', none: 'None', print: 'Print', confirm: 'Confirm',
    },
    nav: {
      dashboard: 'Dashboard', orders: 'Orders', dishes: 'Dishes', tables: 'Tables',
      promotions: 'Promotions', settings: 'Settings', logout: 'Logout',
      brand: 'Dewan-e-Khass', brandSub: 'Owner Dashboard',
    },
    topbar: {
      welcome: 'Welcome back,', activeOrder: 'active order', activeOrders: 'active orders',
    },
    login: {
      title: 'Owner Login', subtitle: 'Sign in to manage your restaurant',
      email: 'Email', password: 'Password', signIn: 'Sign In', signingIn: 'Signing in...',
      forgotPassword: 'Forgot password?', emailPlaceholder: 'you@example.com',
      passwordPlaceholder: 'Enter your password',
    },
    orders: {
      title: 'Order Management', liveActive: 'Live updates active', polling: 'Polling for updates',
      newOrders: 'New Orders', preparing: 'Preparing', ready: 'Ready', history: 'History',
      noNew: 'No New Orders', noNewSub: 'New orders will appear here automatically',
      noPreparing: 'No Orders Preparing', noPreparingSub: 'Orders being prepared will appear here',
      noReady: 'No Ready Orders', noReadySub: 'Ready orders waiting for pickup will appear here',
      accept: 'Accept', cancel: 'Cancel', markReady: 'Mark Ready', setEta: 'Set ETA',
      complete: 'Complete', printReceipt: 'Print Receipt', combinedBill: 'Print Combined Bill',
      markPaidCash: 'Mark Paid (Cash)', markPaidQr: 'Mark Paid (QR)', markUnpaid: 'Mark as Unpaid',
      paid: 'Paid', notPaid: 'Not Paid', readyBy: 'Ready by', more: 'more',
      orderDetails: 'Order Details', orderId: 'Order ID', table: 'Table', status: 'Status',
      payment: 'Payment', orderNote: 'Order Note', orderItems: 'Order Items',
      totalAmount: 'Total Amount', created: 'Created', coupon: 'Coupon', discount: 'Discount',
      estimatedReady: 'Estimated ready:', acceptOrder: 'Accept Order', completeOrder: 'Complete Order',
      setEtaTitle: 'Set Estimated Ready Time', etaQuestion: 'How many minutes until this order is ready?',
      takeaway: 'Takeaway', fromWebsite: 'From Website',
      completed: 'Completed', cancelled: 'Cancelled', items: 'Items', total: 'Total',
      dateTime: 'Date & Time', selected: 'selected', deleteSelected: 'Delete Selected', clear: 'Clear',
      deleteRecords: 'Delete Records', allTime: 'All Time', today: 'Today', thisWeek: 'This Week',
      thisMonth: 'This Month', thisYear: 'This Year', customRange: 'Custom Range',
      allPayments: 'All Payments', totalOrders: 'Total Orders', totalRevenue: 'Total Revenue',
      searchPlaceholder: 'Search by Order ID or Table...',
      salesTax: 'Sales Tax', grandTotal: 'Grand Total',
    },
    dishes: {
      title: 'Dish Management', subtitle: 'dishes on the menu',
      addDish: 'Add Dish', manageCategories: 'Manage Categories', search: 'Search by name or number...',
      available: 'Available', unavailable: 'Unavailable', popular: 'Popular', sale: 'Sale',
      edit: 'Edit', delete: 'Delete', noDishes: 'No dishes yet. Add your first dish!',
      addTitle: 'Add New Dish', editTitle: 'Edit Dish',
      dishNameEn: 'Dish Name (English)', dishNameZh: 'Dish Name (中文)',
      dishNumber: 'Dish Number', dishNumberHint: 'Admin & receipt only — not shown in the mini program.',
      price: 'Price (¥)', discountPrice: 'Discount Price (¥)', category: 'Category',
      categoryEn: 'Category (English)', categoryZh: 'Category (中文)', description: 'Description',
      currentImage: 'Current Image', addImage: 'Add Image (optional)', replaceImage: 'Replace Image (optional)',
      noImage: 'No image', uploadImage: 'Click to upload new image', markPopular: 'Mark as Popular Dish',
      newCategory: '+ New category', useExisting: '← Use existing', selectCategory: 'Select category',
      multiSize: 'Multi-size pricing', useOnePrice: 'Use one price', enabled: 'Enabled',
      createDish: 'Create Dish', updateDish: 'Update Dish', creating: 'Creating...', updating: 'Updating...',
      required: 'required',
      addDishSub: 'Fill in the details to add a dish to the menu', optional: '(optional)',
      dishImage: 'Dish Image', uploadHint: 'Click to upload or drag & drop',
      skipHint: 'Skip for drinks / items without a photo',
      shownMiniZh: 'Shown in the mini program when the user switches to Chinese.',
      shownSidebarZh: 'Shown in the mini program sidebar when the user switches to Chinese.',
      describeDish: 'Describe the dish...', saveDish: 'Save Dish', autoFilled: 'Auto-filled from existing category',
      typeNewCat: 'Type new English category...',
      clearFilters: 'Clear filters',
    },
    tables: {
      title: 'Table Management', occupied: 'Occupied', free: 'Free', addTable: 'Add Table',
      tableNumber: 'Table Number', capacity: 'Capacity (seats)', seats: 'seats',
      markFree: 'Mark Free', removeTable: 'Remove Table', table: 'Table',
      noTables: 'No tables added yet. Click "Add Table" to get started.',
      loadingTables: 'Loading tables...',
      tableQr: 'Table QR code', printAllQr: 'Print All QR', download: 'Download',
      qrHint: 'Print and place on the table. Scanning opens the menu with this table selected.',
      qrGenerating: 'Generating QR codes...',
    },
    promotions: {
      title: 'Promotions', subtitle: 'coupons active', taxDiscountTitle: 'Sales Tax & Discount',
      pageSubtitle: 'Manage sales tax, discount, and coupons', couponsHeading: 'Coupons',
      couponsSub: 'Discount codes customers can apply at checkout',
      taxDiscountSub: 'Applied to every order. Set 0 to disable.',
      salesTax: 'Sales Tax', discount: 'Discount', percent: 'Percentage (%)', fixed: 'Fixed (¥)',
      value: 'Value', saveSettings: 'Save Settings', settingsSaved: 'Tax & discount saved',
      createCoupon: 'Create Coupon', editCoupon: 'Edit Coupon', createNewCoupon: 'Create New Coupon',
      couponCode: 'Coupon Code', discountType: 'Discount Type', discountValue: 'Discount Value',
      minOrder: 'Min Order Amount (¥)', maxUses: 'Max Uses (leave blank = unlimited)', unlimited: 'Unlimited',
      expiryDate: 'Expiry Date', off: 'OFF', min: 'Min', uses: 'uses', exp: 'Exp:', expired: 'Expired',
      noCoupons: 'No coupons yet. Create one to start promoting your restaurant!',
      percentageOption: 'Percentage (%)', fixedOption: 'Fixed Amount (¥)',
    },
    analytics: {
      title: 'Dashboard Overview', subtitle: "Today's performance at a glance",
      todayRevenue: "Today's Revenue", todayOrders: "Today's Orders", totalDishes: 'Total Dishes',
      activeTables: 'Active Tables', revenue: 'Revenue', orders: 'Orders',
      totalOrders: 'Total Orders', avgOrderValue: 'Avg Order Value', activeOrders: 'Active Orders',
      completedOrders: 'completed orders', ordersToday: 'orders placed today', perOrder: 'per order',
      pendingPrepReady: 'pending/preparing/ready',
      thisWeekRevenue: 'This Week Revenue', thisWeekOrders: 'This Week Orders',
      revenueTrend: 'Revenue Trend', ordersByStatus: 'Orders by Status',
      topDishesWeek: 'Top Dishes (this week)', hourlyActivity: 'Hourly Activity (Today)',
      noOrdersToday: 'No orders today', noDataYet: 'No data yet', noOrdersYetToday: 'No orders yet today',
      thisWeek: 'This Week', thisMonth: 'This Month', thisYear: 'This Year',
    },
    settings: {
      title: 'Settings', subtitle: 'Manage your account and preferences', account: 'Account',
      sound: 'Sound Alerts', soundSub: 'Play a sound when a new order arrives',
      language: 'Language', changePassword: 'Change Password',
      email: 'Email', role: 'Role', owner: 'Owner',
      notificationPrefs: 'Notification Preferences', savePreferences: 'Save Preferences',
      pollingInterval: 'Polling Interval (fallback)', pollingSub: 'How often to re-fetch orders if SSE is unavailable',
      loggedInDevices: 'Logged In Devices',
    },
    confirm: {
      cancelOrder: 'Cancel Order', completeOrder: 'Complete Order', deleteForever: 'Delete Forever',
    },
  },

  zh: {
    common: {
      cancel: '取消', save: '保存', create: '创建', update: '更新', delete: '删除',
      edit: '编辑', remove: '移除', refresh: '刷新', loading: '加载中...', saving: '保存中...',
      active: '启用', inactive: '停用', yes: '是', no: '否', close: '关闭', back: '返回',
      search: '搜索', all: '全部', none: '无', print: '打印', confirm: '确认',
    },
    nav: {
      dashboard: '仪表盘', orders: '订单', dishes: '菜品', tables: '餐桌',
      promotions: '促销', settings: '设置', logout: '退出登录',
      brand: 'Dewan-e-Khass', brandSub: '商家后台',
    },
    topbar: {
      welcome: '欢迎回来,', activeOrder: '个进行中订单', activeOrders: '个进行中订单',
    },
    login: {
      title: '商家登录', subtitle: '登录以管理您的餐厅',
      email: '邮箱', password: '密码', signIn: '登录', signingIn: '登录中...',
      forgotPassword: '忘记密码?', emailPlaceholder: 'you@example.com',
      passwordPlaceholder: '请输入密码',
    },
    orders: {
      title: '订单管理', liveActive: '实时更新中', polling: '轮询更新中',
      newOrders: '新订单', preparing: '制作中', ready: '已就绪', history: '历史',
      noNew: '暂无新订单', noNewSub: '新订单会自动显示在此',
      noPreparing: '暂无制作中订单', noPreparingSub: '正在制作的订单会显示在此',
      noReady: '暂无就绪订单', noReadySub: '等待取餐的订单会显示在此',
      accept: '接单', cancel: '取消', markReady: '标记就绪', setEta: '设置预计时间',
      complete: '完成', printReceipt: '打印小票', combinedBill: '打印合并账单',
      markPaidCash: '标记已付(现金)', markPaidQr: '标记已付(扫码)', markUnpaid: '标记未付',
      paid: '已付', notPaid: '未付', readyBy: '预计完成', more: '更多',
      orderDetails: '订单详情', orderId: '订单号', table: '餐桌', status: '状态',
      payment: '支付', orderNote: '订单备注', orderItems: '订单菜品',
      totalAmount: '总金额', created: '创建时间', coupon: '优惠券', discount: '折扣',
      estimatedReady: '预计完成:', acceptOrder: '接受订单', completeOrder: '完成订单',
      setEtaTitle: '设置预计完成时间', etaQuestion: '还需多少分钟完成?',
      takeaway: '打包带走', fromWebsite: '来自网站',
      completed: '已完成', cancelled: '已取消', items: '菜品', total: '总计',
      dateTime: '日期时间', selected: '已选', deleteSelected: '删除所选', clear: '清除',
      deleteRecords: '删除记录', allTime: '全部时间', today: '今天', thisWeek: '本周',
      thisMonth: '本月', thisYear: '今年', customRange: '自定义范围',
      allPayments: '全部支付', totalOrders: '订单总数', totalRevenue: '总收入',
      searchPlaceholder: '按订单号或餐桌搜索...',
      salesTax: '销售税', grandTotal: '总计',
    },
    dishes: {
      title: '菜品管理', subtitle: '道菜在菜单上',
      addDish: '添加菜品', manageCategories: '管理分类', search: '按名称或编号搜索...',
      available: '上架', unavailable: '下架', popular: '热门', sale: '特价',
      edit: '编辑', delete: '删除', noDishes: '暂无菜品,添加您的第一道菜!',
      addTitle: '添加新菜品', editTitle: '编辑菜品',
      dishNameEn: '菜品名称(英文)', dishNameZh: '菜品名称(中文)',
      dishNumber: '菜品编号', dishNumberHint: '仅用于后台和小票,不在小程序显示。',
      price: '价格(¥)', discountPrice: '折扣价(¥)', category: '分类',
      categoryEn: '分类(英文)', categoryZh: '分类(中文)', description: '菜品介绍',
      currentImage: '当前图片', addImage: '添加图片(可选)', replaceImage: '更换图片(可选)',
      noImage: '无图片', uploadImage: '点击上传新图片', markPopular: '标记为热门菜品',
      newCategory: '+ 新分类', useExisting: '← 使用现有', selectCategory: '选择分类',
      multiSize: '多规格定价', useOnePrice: '使用单一价格', enabled: '已启用',
      createDish: '创建菜品', updateDish: '更新菜品', creating: '创建中...', updating: '更新中...',
      required: '必填',
      addDishSub: '填写详情以将菜品添加到菜单', optional: '(可选)',
      dishImage: '菜品图片', uploadHint: '点击上传或拖放',
      skipHint: '饮品/无图片菜品可跳过',
      shownMiniZh: '用户切换中文时在小程序显示。',
      shownSidebarZh: '用户切换中文时在小程序侧栏显示。',
      describeDish: '描述这道菜...', saveDish: '保存菜品', autoFilled: '从现有分类自动填充',
      typeNewCat: '输入新的英文分类...',
      clearFilters: '清除筛选',
    },
    tables: {
      title: '餐桌管理', occupied: '占用', free: '空闲', addTable: '添加餐桌',
      tableNumber: '桌号', capacity: '容纳(座位)', seats: '座位',
      markFree: '标记空闲', removeTable: '移除餐桌', table: '餐桌',
      noTables: '暂无餐桌,点击"添加餐桌"开始。',
      loadingTables: '正在加载餐桌...',
      tableQr: '桌位二维码', printAllQr: '打印全部二维码', download: '下载',
      qrHint: '打印后贴在餐桌上。顾客扫码即可打开菜单并自动选择该桌位。',
      qrGenerating: '正在生成二维码...',
    },
    promotions: {
      title: '促销', subtitle: '个有效优惠券', taxDiscountTitle: '销售税与折扣',
      pageSubtitle: '管理销售税、折扣和优惠券', couponsHeading: '优惠券',
      couponsSub: '顾客可在结算时使用的折扣码',
      taxDiscountSub: '应用于每个订单,设为0则停用。',
      salesTax: '销售税', discount: '折扣', percent: '百分比(%)', fixed: '固定(¥)',
      value: '数值', saveSettings: '保存设置', settingsSaved: '税与折扣已保存',
      createCoupon: '创建优惠券', editCoupon: '编辑优惠券', createNewCoupon: '创建新优惠券',
      couponCode: '优惠券码', discountType: '折扣类型', discountValue: '折扣值',
      minOrder: '最低订单金额(¥)', maxUses: '最大使用次数(留空=无限)', unlimited: '无限',
      expiryDate: '到期日期', off: '优惠', min: '最低', uses: '次使用', exp: '到期:', expired: '已过期',
      noCoupons: '暂无优惠券,创建一个开始推广您的餐厅!',
      percentageOption: '百分比(%)', fixedOption: '固定金额(¥)',
    },
    analytics: {
      title: '仪表盘概览', subtitle: '今日业绩一览',
      todayRevenue: '今日收入', todayOrders: '今日订单', totalDishes: '菜品总数',
      activeTables: '使用中餐桌', revenue: '收入', orders: '订单',
      totalOrders: '订单总数', avgOrderValue: '平均订单金额', activeOrders: '进行中订单',
      completedOrders: '已完成订单', ordersToday: '今日下单', perOrder: '每单',
      pendingPrepReady: '待处理/制作中/就绪',
      thisWeekRevenue: '本周收入', thisWeekOrders: '本周订单',
      revenueTrend: '收入趋势', ordersByStatus: '订单状态分布',
      topDishesWeek: '热门菜品(本周)', hourlyActivity: '每小时活动(今日)',
      noOrdersToday: '今日暂无订单', noDataYet: '暂无数据', noOrdersYetToday: '今日暂无订单',
      thisWeek: '本周', thisMonth: '本月', thisYear: '今年',
    },
    settings: {
      title: '设置', subtitle: '管理您的账户与偏好', account: '账户',
      sound: '声音提醒', soundSub: '新订单到达时播放声音',
      language: '语言', changePassword: '修改密码',
      email: '邮箱', role: '角色', owner: '店主',
      notificationPrefs: '通知偏好', savePreferences: '保存偏好',
      pollingInterval: '轮询间隔(备用)', pollingSub: 'SSE不可用时重新获取订单的频率',
      loggedInDevices: '已登录设备',
    },
    confirm: {
      cancelOrder: '取消订单', completeOrder: '完成订单', deleteForever: '永久删除',
    },
  },
};

export default STRINGS;
